'use client';

import React from 'react';
import classNames from 'classnames';
import WhatToExpect from 'src/components/WhatToExpect/WhatToExpect';
import Heading from 'src/components/Heading/Heading';
import List, { ListItemType } from 'src/components/List/List';
import styles from './section-timeline.module.scss';
import getContentGroups from 'src/helpers/getContentGroups';
import { Button } from '@get-mosh/msh-shared';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { SliceComponentProps } from '@prismicio/react';
import { Content, RichTextField } from '@prismicio/client';

type SectionTimelineProps =
    SliceComponentProps<Content.SectionTimelineSlice> & {
        context: {
            pageType: string;
        };
    };

const SectionTimeline = ({ slice, context }: SectionTimelineProps) => {
    const getDataForColumns = () => {
        return {
            disclaimer: slice.primary.disclaimer || undefined,
            accordion: [
                {
                    title: slice.primary.column_one_title || '',
                    subtitle: slice.primary.column_one_subtitle || '',
                    content: (
                        <RenderMoshList
                            field={slice.primary.column_one_content}
                        />
                    ),
                    icon: {
                        src: slice.primary.column_one_image.square.url || '',
                        alt: slice.primary.column_one_image.alt || '',
                        width: slice.primary.column_one_image.square.dimensions
                            ? slice.primary.column_one_image.square.dimensions
                                  .width / 2
                            : 0,
                        height: slice.primary.column_one_image.square.dimensions
                            ? slice.primary.column_one_image.square.dimensions
                                  .height / 2
                            : 0,
                    },
                },
                {
                    title: slice.primary.column_two_title || '',
                    subtitle: slice.primary.column_two_subtitle || '',
                    content: (
                        <RenderMoshList
                            field={slice.primary.column_two_content}
                        />
                    ),
                    icon: {
                        src: slice.primary.column_two_image.square.url || '',
                        alt: slice.primary.column_two_image.alt || '',
                        width: slice.primary.column_two_image.square.dimensions
                            ? slice.primary.column_two_image.square.dimensions
                                  .width / 2
                            : 0,
                        height: slice.primary.column_two_image.square.dimensions
                            ? slice.primary.column_two_image.square.dimensions
                                  .height / 2
                            : 0,
                    },
                },
                {
                    title: slice.primary.column_three_title || '',
                    subtitle: slice.primary.column_three_subtitle || '',
                    content: (
                        <RenderMoshList
                            field={slice.primary.column_three_content}
                        />
                    ),
                    icon: {
                        src: slice.primary.column_three_image.square.url || '',
                        alt: slice.primary.column_three_image.alt || '',
                        width: slice.primary.column_three_image.square
                            .dimensions
                            ? slice.primary.column_three_image.square.dimensions
                                  .width / 2
                            : 0,
                        height: slice.primary.column_three_image.square
                            .dimensions
                            ? slice.primary.column_three_image.square.dimensions
                                  .height / 2
                            : 0,
                    },
                },
            ],
            columns: [
                {
                    period: slice.primary.column_one_title || '',
                    stage: slice.primary.column_one_subtitle || '',
                    contentTypeNode: true,
                    children: (
                        <RenderMoshList
                            field={slice.primary.column_one_content}
                        />
                    ),
                    icon: {
                        src: slice.primary.column_one_image.square.url || '',
                        alt: slice.primary.column_one_image.alt || '',
                        width: slice.primary.column_one_image.square.dimensions
                            ? slice.primary.column_one_image.square.dimensions
                                  .width / 2
                            : 0,
                        height: slice.primary.column_one_image.square.dimensions
                            ? slice.primary.column_one_image.square.dimensions
                                  .height / 2
                            : 0,
                    },
                },
                {
                    period: slice.primary.column_two_title || '',
                    stage: slice.primary.column_two_subtitle || '',
                    contentTypeNode: true,
                    children: (
                        <RenderMoshList
                            field={slice.primary.column_two_content}
                        />
                    ),
                    icon: {
                        src: slice.primary.column_two_image.square.url || '',
                        alt: slice.primary.column_two_image.alt || '',
                        width: slice.primary.column_two_image.square.dimensions
                            ? slice.primary.column_two_image.square.dimensions
                                  .width / 2
                            : 0,
                        height: slice.primary.column_two_image.square.dimensions
                            ? slice.primary.column_two_image.square.dimensions
                                  .height / 2
                            : 0,
                    },
                },
                {
                    period: slice.primary.column_three_title || '',
                    stage: slice.primary.column_three_subtitle || '',
                    contentTypeNode: true,
                    children: (
                        <RenderMoshList
                            field={slice.primary.column_three_content}
                        />
                    ),
                    icon: {
                        src: slice.primary.column_three_image.square.url || '',
                        alt: slice.primary.column_three_image.alt || '',
                        width: slice.primary.column_three_image.square
                            .dimensions
                            ? slice.primary.column_three_image.square.dimensions
                                  .width / 2
                            : 0,
                        height: slice.primary.column_three_image.square
                            .dimensions
                            ? slice.primary.column_three_image.square.dimensions
                                  .height / 2
                            : 0,
                    },
                },
            ],
        };
    };

    return (
        <section
            style={{ backgroundColor: slice.primary.background || undefined }}
            className={classNames('py-8 lg:py-16', styles['wrapper'])}
        >
            <div className="container">
                <Heading level={'h2'} centered={true}>
                    {slice.primary.title}
                </Heading>
                <WhatToExpect {...getDataForColumns()} />
                {slice.primary.cta_label && slice.primary.cta_link && (
                    <div className="mt-8 flex items-center justify-center">
                        <Button
                            text={slice.primary.cta_label}
                            as="a"
                            href={slice.primary.cta_link}
                            onClick={() => {
                                trackClick(EventNames.BUTTON_CLICKED, {
                                    pageType: context.pageType,
                                    label: slice.primary.cta_label || '',
                                    link: slice.primary.cta_link || '',
                                    variation: slice.variation,
                                    location: slice.slice_type,
                                });
                            }}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

type RenderMoshListProps = {
    field: RichTextField;
};

const RenderMoshList = ({ field }: RenderMoshListProps) => {
    const contentGroups = getContentGroups(field);
    return (
        <>
            {contentGroups.map((e, i) => (
                <List
                    className={'mb-4'}
                    items={e as ListItemType[]}
                    key={i}
                    icon={{
                        faIcon: faChevronRight,
                        background: 'bg-none',
                        colour: 'text-green',
                        border: 'border-none',
                    }}
                />
            ))}
        </>
    );
};

export default SectionTimeline;
